import React, { useCallback, useRef, useState } from "react";
import { useDroppable } from "@dnd-kit/react";

import InfoCircleIcon from "../../../../../../../resources/info-circle.svg";
import {
  UNASSIGNED_ROUTE_ID,
  useRouteBuilder,
} from "../../../context/RouteBuilderContext";
import { maskImageStyle } from "../../../../../../../utils/maskImageStyle";
import SearchBar from "../../../../../../../components/Filters/SearchBarWithFilters";
import SubtleButton from "../../../../../../../components/Buttons/SubtleButton";
import { Tooltip } from "../../../../../../../components/SiteMap/Tooltip";
import { useStopSequence } from "../stops/useStopSequence";
import { unassignedVisits } from "../../../lib/addUnassignedCatalog";
import AddToUnassignedPopover from "./AddToUnassignedPopover";

export default function UnassignedPool({
  search,
  onSearch,
}: {
  search: string;
  onSearch: (value: string) => void;
}) {
  const { session } = useRouteBuilder();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [adding, setAdding] = useState(false);
  const closePopup = useCallback(() => setAdding(false), []);
  const { ref, isDropTarget } = useDroppable({
    id: "unassigned-pool-drop",
    type: "route-column",
    accept: "stop",
    collisionPriority: 5,
    data: { routeId: UNASSIGNED_ROUTE_ID },
  });
  const visits = session ? unassignedVisits(session, search) : [];
  const stopItems = useStopSequence(
    UNASSIGNED_ROUTE_ID,
    visits,
    "bg-amber-600",
  );
  if (!session) return null;
  const count = session.unroutedJobs.length;

  return (
    <div ref={anchorRef} className="h-full">
      <section
        ref={ref}
        data-testid="unassigned-pool"
        className={`flex h-full min-h-[120px] flex-col overflow-hidden rounded-xl border border-dashed border-amber-300 bg-amber-50/60 ${
          isDropTarget ? "ring-2 ring-inset ring-amber-400" : ""
        }`}
      >
        <header className="shrink-0 border-b border-amber-200 px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-amber-900">
              Unassigned{" "}
              <span
                data-testid="unassigned-count"
                className="ml-1 rounded-full bg-amber-600 px-1.5 py-0.5 text-[10px] text-white"
              >
                {count}
              </span>
            </h3>
            <div className="flex [&_.has-tooltip]:flex [&_.has-tooltip:focus-within_.tooltip-text]:visible [&_.tooltip-text]:left-auto [&_.tooltip-text]:right-0 [&_.tooltip-text]:ml-0 [&_.tooltip-text]:w-56 [&_.tooltip-text]:text-left [&_.tooltip-text]:text-xs [&_.tooltip-text]:shadow-lg [&_.tooltip-text::after]:left-auto [&_.tooltip-text::after]:right-1">
              <Tooltip tooltip="Properties the generated plan couldn't place, plus any you add here. Saved with this draft.">
                <button
                  type="button"
                  aria-label="What are unassigned properties?"
                  className="flex rounded-full text-amber-700 hover:text-amber-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
                >
                  <span
                    aria-hidden
                    className="h-4 w-4 bg-current"
                    style={maskImageStyle(InfoCircleIcon)}
                  />
                </button>
              </Tooltip>
            </div>
          </div>
          <p className="text-[11px] text-amber-800/80">Saved with this draft</p>
          <div className="mt-2 space-y-2">
            <SearchBar
              variant="outlined"
              label="Search unassigned"
              testId="unassigned-search"
              placeholder="Search unassigned"
              value={search}
              onChange={onSearch}
              onSearch={onSearch}
              autoFocus={false}
            />
            <SubtleButton
              testId="add-unassigned-properties"
              className="w-full !border-amber-400 !py-1.5 !text-xs !text-amber-900"
              onClick={() => setAdding(true)}
            >
              + Add properties
            </SubtleButton>
          </div>
        </header>
        <ul className="min-h-0 flex-1 space-y-2 overflow-y-auto p-2">
          {visits.length === 0 ? (
            <li className="px-3 py-6 text-center text-xs text-amber-900">
              {count === 0
                ? "Nothing here yet. Use Add properties to find some."
                : "No matching properties."}
            </li>
          ) : null}
          {stopItems}
        </ul>
        <footer className="shrink-0 border-t border-amber-200 px-3 py-2 text-xs text-slate-600">
          Drag a card onto a route to add it. Drop it between two stops to pick
          its place.
        </footer>
      </section>
      {adding ? (
        <AddToUnassignedPopover anchorRef={anchorRef} onClose={closePopup} />
      ) : null}
    </div>
  );
}
